'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';

import { LanguageSelector, ThemeToggleItem } from '@/components';
import { useAuth, useT } from '@/hooks';
import { useAppDispatch } from '@/store/hooks';
import { openModal } from '@/store/slices/ui.slice';
import type { BaseMenuItemConfig } from '@/types';

import { PRIMARY_MENU_ITEMS, ROUTES, SECONDARY_MENU_ITEMS } from '@/constants';

interface MobileMenuDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
  onThemeToggle: () => void;
}

const MobileMenuItem = ({
  item,
  onClick,
  isSecondary = false,
}: {
  item: BaseMenuItemConfig;
  onClick: (path: string | null) => void;
  isSecondary?: boolean;
}) => {
  const { t } = useT();
  const handleClick = useCallback(() => {
    onClick(item.path);
  }, [onClick, item.path]);

  return (
    <ListItem disablePadding>
      <ListItemButton
        onClick={handleClick}
        sx={{
          py: 1.5,
          px: 2,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <ListItemText
          primary={t(item.label)}
          primaryTypographyProps={{
            fontSize: '0.875rem',
            fontWeight: isSecondary ? 400 : 500,
            color: isSecondary ? 'text.secondary' : 'text.primary',
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

export const MobileMenuDrawer = ({
  isOpen,
  onClose,
  isDarkMode,
  onThemeToggle,
}: MobileMenuDrawerProps) => {
  const { t } = useT();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { user } = useAuth();

  const handleItemClick = useCallback(
    (path: string | null) => {
      if (path === ROUTES.REFERRALS && !user?.isReferredUser) {
        dispatch(openModal('referral'));
        onClose();
      } else if (path) {
        router.push(path);
        onClose();
      }
    },
    [router, onClose, user?.isReferredUser, dispatch]
  );

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 280,
          bgcolor: 'background.paper',
        },
      }}
    >
      <Box sx={{ py: 2 }}>
        <Typography variant="h6" sx={{ px: 2, mb: 2, fontWeight: 700 }}>
          {t('nav.more')}
        </Typography>

        <List>
          {PRIMARY_MENU_ITEMS.map((item) => (
            <MobileMenuItem key={item.label} item={item} onClick={handleItemClick} />
          ))}
        </List>

        <Divider sx={{ my: 1 }} />

        <List>
          {SECONDARY_MENU_ITEMS.map((item) => (
            <MobileMenuItem key={item.label} item={item} isSecondary onClick={handleItemClick} />
          ))}
        </List>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ px: 1 }}>
          <ThemeToggleItem isDarkMode={isDarkMode} onToggle={onThemeToggle} />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ px: 2, pb: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ letterSpacing: 0.5, display: 'block', mb: 1 }}
          >
            {t('menu.selectLanguage')}
          </Typography>
          <LanguageSelector compact={false} />
        </Box>
      </Box>
    </Drawer>
  );
};

export default MobileMenuDrawer;
